import { Log } from '../AuxillaryService/Log';
import { ReachAPI } from '../ReachApi';
import { BaseWidgetConfiguration, TimelineSlot, Widget, WidgetState } from '../Widget';
import { WidgetFactory } from '../Widget/WidgetFactory';
import { ITimeline } from '../Timeline';
import { $ } from '../../vendor/jquery';
import { EventEmitterHelper } from '../Utility/EventEmitter';
import { ZodZoneConfiguration, ZoneConfiguration, ZoneInfo } from './types';
import { isNullish } from '../Utility/Nullish';

const FULLSCREEN_CSS_CLASS = 'zone--fullscreen-announcement-active';

/**
 * Extracts the id property from an unknown object if it exists or returns
 * "unknown" if the property does not exist.
 */
function getZoneIdFromMaybeConfiguration(maybeZoneConfiguration: unknown): string {
    if (maybeZoneConfiguration instanceof Object && 'id' in maybeZoneConfiguration) {
        const zoneId = Reflect.get(maybeZoneConfiguration, 'id');

        if (!isNullish(zoneId)) {
            return String(zoneId);
        }
    }

    return 'unknown';
}

export class Zone extends EventEmitterHelper {
    /**
     * Zone ID
     */
    public readonly id: number;

    /**
     * Background Color of Zone
     */
    public readonly backgroundColor: string | null;

    /**
     * Background Image of Zone
     */
    public readonly backgroundImage: string | null;

    /**
     * CSS to apply to Zone
     */
    public readonly css: string | null;

    /**
     * Default duration for widget in zone
     * todo: Remove this, widget's job
     */
    private duration: number;

    /**
     * Time between fade transition
     */
    private fadeDuration: number;

    /**
     * Zone Height
     */
    public readonly height: number;

    /**
     * A boolean describing if the zone is paused
     */
    #isPaused: boolean;
    get isPaused(): boolean {
        return this.#isPaused;
    }

    /**
     * Name of Zone
     */
    public readonly name: string;

    /**
     * EventHandler - triggered on loop complete
     */
    private onLoopCompleteString: string | null;

    /**
     * EventHandler - triggered on zone initialized
     */
    private onZoneInitializedString: string | null;

    /**
     * Timeline Type
     */
    private type: string;

    /**
     * An array of widget ids that belong to this zone
     */
    private widgetIds: number[];

    /**
     * List of widgets
     */
    public widgets: Widget[];

    /**
     * Zone Width
     */
    public readonly width: number;

    /**
     * Zone X value
     */
    public readonly x: number;

    /**
     * Zone Y value
     */
    public readonly y: number;

    /**
     * Zone z-index
     */
    public readonly z: number;

    /**
     * A boolean describing if the Zone has fully initialized
     */
    #initialized: boolean;
    get initialized(): boolean {
        return this.#initialized;
    }

    /**
     * Interruptions Array
     */
    private interruptions: Record<string, JQuery.Deferred<void>[]>;

    /**
     * Total number of retries with no slots to play
     */
    private retriesWithNoSlots: number;

    /**
     * @todo: figure out shape and purpose
     * Variable(`timeoutID`) to handle zone timeout
     */
    private timeoutVariable: ReturnType<typeof setTimeout> | null;

    /**
     * Zone's Timeline
     */
    private zoneTimeline: ITimeline;

    /**
     * Zone's Triggers
     */
    private triggers: {
        onZoneInitialized: () => void;
        onLoopComplete: () => void;
    };

    constructor(maybeZoneConfiguration: ZoneConfiguration, timeline: ITimeline) {
        super();

        const zoneConfiguration = this.validateConfiguration(maybeZoneConfiguration);

        this.id = zoneConfiguration.id;
        this.css = zoneConfiguration.css ?? null;
        this.backgroundColor = zoneConfiguration.background_color ?? null; //todo: why is this different than css?
        this.backgroundImage = zoneConfiguration.default_img ?? null;
        this.duration = zoneConfiguration.duration ?? 10;
        this.fadeDuration = zoneConfiguration.fade_duration ?? 1000;
        this.name = zoneConfiguration.name ?? null;
        this.type = zoneConfiguration.type ?? '';
        this.width = zoneConfiguration.width ?? null;
        this.height = zoneConfiguration.height ?? null;
        this.x = zoneConfiguration.x ?? null;
        this.y = zoneConfiguration.y ?? null;
        this.z = zoneConfiguration.z ?? null;

        this.widgetIds = zoneConfiguration.widget_ids;
        this.widgets = [];

        this.#isPaused = false;
        this.#initialized = false;
        this.interruptions = {};
        this.retriesWithNoSlots = 0;
        this.timeoutVariable = null;

        this.onLoopCompleteString = zoneConfiguration.on_loop_complete ?? null;
        this.onZoneInitializedString = zoneConfiguration.on_zone_initialized ?? null;

        this.triggers = {
            onZoneInitialized: (): void => {
                if (this.onZoneInitializedString) {
                    eval(this.onZoneInitializedString);
                }
            },
            onLoopComplete: (): void => {
                if (this.onLoopCompleteString) {
                    eval(this.onLoopCompleteString);
                }
            },
        };

        this.zoneTimeline = timeline;
    }

    get wrapperElement(): HTMLElement {
        const wrapperElement = document.getElementById(`zone_${this.id}`);
        if (!wrapperElement) {
            throw new Error(`No Zone wrapperElement found for zone with id: ${this.id}`);
        }

        return wrapperElement;
    }

    protected validateConfiguration(maybeZoneConfiguration: unknown): ZoneConfiguration {
        try {
            return ZodZoneConfiguration.parse(maybeZoneConfiguration);
        } catch (error) {
            //Invalid!  Currently, we have no course correction, but at least we can be warned
            Log.Warning(
                `Zone (id=${getZoneIdFromMaybeConfiguration(
                    maybeZoneConfiguration
                )}) configuration did not pass validation`,
                null,
                __fileline
            );

            return maybeZoneConfiguration as ZoneConfiguration;
        }
    }

    /**
     * Additional class construction to do after DOM elements have been created.
     */
    construct(): void {
        this.setEventEmitterElement(() => this.wrapperElement);
        this.eventEmitter.addListener(
            'show-fullscreen-announcement',
            this._onFullscreenAnnouncement.bind(this)
        );
        this.eventEmitter.addListener(
            'hide-fullscreen-announcement',
            this._onFullscreenAnnouncement.bind(this)
        );
    }

    /**
     * clears cache on all widgets
     */
    clearCache(): void {
        this.widgets.forEach(function (w) {
            try {
                w.clearCache();
            } catch (e2) {
                // The original developer failed to document what exception they
                // were trying to catch, so it is unknown why this try/catch
                // block is here.
            }
        });
    }

    /**
     * returns object with specific zone information
     */
    returnZoneInfo(): ZoneInfo {
        return {
            id: this.id,
            type: this.type,
            x: this.x,
            y: this.y,
            z: this.z,
            w: this.width,
            h: this.height,
            css: this.css,
        };
    }

    /**
     * pauses zone and all widgets
     */
    async pause(): Promise<void> {
        this.#isPaused = true;

        if (this.timeoutVariable) {
            clearTimeout(this.timeoutVariable);
            this.timeoutVariable = null;
        }

        // pause() could be called before timeline exists
        const currentTimelineSlot = this.zoneTimeline.getCurrentSlot();
        if (!currentTimelineSlot) {
            Log.Warning('The current Timeline Slot does not exist.', null, __fileline);
        } else {
            for (const w of this.widgets) {
                try {
                    await w.pause(currentTimelineSlot);
                } catch (reason) {
                    Log.Error(
                        'Widget failed to pause. Error message: ' + String(reason),
                        null,
                        __fileline
                    );
                }
            }
        }
    }

    /**
     * Calls beforeFadeOut and afterFadeOut on slot and preps for next
     */
    setZoneTimeout(widget: Widget, slot: TimelineSlot, duration: number, timeout: number): void {
        if (!this.#isPaused) {
            this.timeoutVariable = setTimeout(async () => {
                this.timeoutVariable = null;

                const fadeDuration = this.fadeDuration / 2;

                const nextSlot = this.zoneTimeline.peakNext();

                const result = await widget.beforeFadeOut(slot, nextSlot);
                if (!result || !result.cancelFade) {
                    await $(slot.widget.wrapperElement)
                        .animate({ opacity: 0 }, fadeDuration, 'linear')
                        .promise();
                    await widget.afterFadeOut(slot, nextSlot);

                    // if the whole zone was stopped: no need to
                    // run pause or prepare next widget
                    if (!this.#isPaused) {
                        widget.pause(slot);

                        this.prepareWidgets(duration, false, false);
                    }
                } else {
                    // Do not pause because we are cancelling the
                    // fadeout. That most likely means that this is the
                    // only item in the zone (1 item in timeline).
                    this.prepareWidgets(duration, true, result.suppressTriggers === true);
                }
            }, timeout);
        }
    }

    /**
     * Once Zone is initialized, create timeline and prepare widgets
     */
    runWidgets(cancelFade: boolean): void {
        if (this.type == 'fullscreen') {
            this.jqWrapperElement.css({
                'z-index': '999999',
                display: 'block',
            });
        }

        //todo: why does runWidgets then prepareWidgets?
        this.prepareWidgets(this.duration, cancelFade, false);
    }

    /**
     * Prepares the previous, current and next slot for showing, hiding ect
     */
    prepareWidgets(duration: number, cancelFade: boolean, suppressTriggers: boolean): void {
        if (!this.#isPaused) {
            const previousSlot = this.zoneTimeline.getCurrentSlot();
            const currentSlot = this.zoneTimeline.nextSlot(suppressTriggers, this);
            const nextSlot = this.zoneTimeline.peakNext();

            if (isNullish(currentSlot)) {
                //Zone will continue to find widgets
                this.retriesWithNoSlots++;
                setTimeout(
                    () => {
                        this.prepareWidgets(duration, false, true);
                    },
                    this.retriesWithNoSlots < 20 ? 125 : 1000
                );

                return;
            } else {
                this.retriesWithNoSlots = 0;
            }

            this.showWidget(duration, cancelFade, previousSlot, currentSlot, nextSlot);
        }
    }

    /**
     * Calls beforeFadeOut and afterFadeOut on slot and preps for next
     * @params duration - Seconds
     */
    async showWidget(
        duration: number,
        cancelFade: boolean,
        previousSlot: TimelineSlot | null,
        currentSlot: TimelineSlot,
        nextSlot: TimelineSlot | null
    ): Promise<void> {
        duration = currentSlot.duration;
        const fadeDuration = this.fadeDuration / 2;

        const sameWidgetIsUpNext = currentSlot.widget.id === nextSlot?.widget.id;
        if (!sameWidgetIsUpNext) {
            // make sure that all of the other widgets are not visible
            // at this point (should not be, but sometimes this clean-up
            // is needed)
            this.widgets.forEach(function (widget) {
                widget.wrapperElement.style.opacity = '0';
            });
        }

        if (currentSlot.widget.state === WidgetState.STOPPED) {
            // use getReadyForDisplay here to
            // bring up any visualizations
            // that can fade in if need be
            await currentSlot.widget.getReadyForDisplay(currentSlot);
        }

        const beforeFadeInCommand = await currentSlot.widget.beforeFadeIn(
            currentSlot,
            previousSlot,
            this.zoneTimeline
        );
        let abortLoop = beforeFadeInCommand?.abortLoop;

        // @todo: should we bother running this code if the the loop has been aborted?
        if (!cancelFade) {
            // the fade may be canceled, but the widget should still be playing
            // (1 item in timeline). IE no pausing, resuming or fading.

            await $(currentSlot.widget.wrapperElement)
                .animate({ opacity: 1.0 }, fadeDuration, 'linear')
                .promise();

            const afterFadeInCommand = await currentSlot.widget.afterFadeIn(
                currentSlot,
                previousSlot
            );
            abortLoop = afterFadeInCommand?.abortLoop || beforeFadeInCommand?.abortLoop;
        }

        let playFailure = false;
        if (abortLoop) {
            this.zoneTimeline.abortLoop();
        } else {
            if (currentSlot.widget.isPaused) {
                try {
                    await currentSlot.widget.play(currentSlot);
                } catch (err) {
                    // @todo Send this log to the server. Widget slots that fail to play need to be
                    // fixed even if the Zone lifecycle can continue without them.
                    Log.Critical(`Cannot play widget. ${String(err)}`, null, __fileline, {
                        error: err,
                        widget: {
                            id: currentSlot.widget.id,
                            name: currentSlot.widget.name,
                        },
                    });
                    playFailure = true;
                }
            }

            if (nextSlot?.widget) {
                void nextSlot.widget.contentOnDeck(nextSlot);
            }
        }

        if (playFailure) {
            // Slot failed to play, skipping.
            this.setZoneTimeout(currentSlot.widget, currentSlot, 0, 0);
        } else {
            this.setZoneTimeout(
                currentSlot.widget,
                currentSlot,
                duration,
                currentSlot.duration * 1000
            );
        }
    }

    /**
     * Stops all of the widgets in the current zone.
     */
    async stop(): Promise<void> {
        this.#isPaused = true;
        if (this.timeoutVariable) {
            clearTimeout(this.timeoutVariable);
        }

        this.wrapperElement.classList.remove(FULLSCREEN_CSS_CLASS);
        this.widgets.forEach(function (widget) {
            widget.wrapperElement.style.opacity = '0';

            try {
                widget.stop();
            } catch (error) {
                Log.Error(
                    'Widget failed to stop. Error message: ' + String(error),
                    null,
                    __fileline
                );
            }
        });
    }

    /**
     * Plays the widgets in the timeline's loop, starting from the beginning
     * of the loop.
     */
    async play(cancelFade: boolean): Promise<void> {
        if (this.#isPaused) {
            this.#isPaused = false;
            this.runWidgets(cancelFade);
        }

        return Promise.resolve();
    }

    /**
     * Gets event interruptions
     * @todo: give unknown resolution a proper type
     * @returns {Array.<jQuery.promise>} Array of interruptions with eventName
     */
    getEventInterruptions(eventName: string): JQuery.Deferred<unknown>[] {
        if (typeof this.interruptions[eventName] !== 'undefined') {
            const result = this.interruptions[eventName];

            //interruptions get cleared after they are used
            this.interruptions[eventName] = [];

            return result;
        }

        return [];
    }

    /**
     * Gets trigger by name
     * @todo: refactor with triggers enum
     * @todo remove - pointless unless you want to turn the return member
     */
    getTrigger(name: 'onZoneInitialized' | 'onLoopComplete'): (() => void) | null {
        return typeof this.triggers[name] === 'function' ? this.triggers[name].bind(this) : null;
    }

    /**
     * Gets widget config from API and calls initializeWidget with API response
     */
    private async getWidgets(widgetIdArray: number[]): Promise<Widget[]> {
        const widgets: Widget[] = [];

        const widgetInitializationPromises = widgetIdArray.map((widgetId) => {
            return new Promise<void>((resolve, reject) => {
                ReachAPI.signage
                    .getWidgetConfig(widgetId)
                    .done((response: { widget: BaseWidgetConfiguration }) => {
                        widgets.push(WidgetFactory.createWidget(response.widget, this.id));
                        resolve();
                    })
                    .fail(
                        (
                            jqXHR: JQuery.jqXHR,
                            textStatus: string,
                            errorThrown: string,
                            msg?: string,
                            event?: Event
                        ) => {
                            Log.Warning(
                                `Failed to get Config for Widget: ${widgetId}`,
                                null,
                                __fileline,
                                ReachAPI.createPromiseFailObject.apply(this, [
                                    jqXHR,
                                    textStatus,
                                    errorThrown,
                                    msg ?? null,
                                    event ?? null,
                                ])
                            );
                            reject(new Error(`Failed to get Config for Widget: ${widgetId}`));
                        }
                    );
            });
        });

        //Wait for all widgets to initialize or fail,
        // then finalize initialization and run
        await Promise.allSettled(widgetInitializationPromises);
        return widgets;
    }

    async initialize(): Promise<void> {
        this.widgets = await this.getWidgets(this.widgetIds);
        this.widgets.forEach((w) => this.wrapperElement.appendChild(w.wrapperElement));
        await Promise.allSettled(this.widgets.map((w) => w.initialize()));
        this.zoneTimeline.sortWidgets(this.widgets);
        this.#initialized = true;
        const onInitTrigger = this.getTrigger('onZoneInitialized');

        if (onInitTrigger) {
            onInitTrigger(); //fire the Zone Initialized trigger (if there is one)
        }

        this.runWidgets(false);
    }

    /**
     * Hides Zone
     */
    hide(): void {
        this.jqWrapperElement.animate({ opacity: 0 }, 500, 'linear');
    }

    /**
     * Shows Zone
     */
    show(): void {
        this.jqWrapperElement.animate({ opacity: 1.0 }, 500, 'linear');
    }

    isHidden(): boolean {
        return this.jqWrapperElement.css('opacity') === '0';
    }

    get jqWrapperElement(): JQuery<HTMLElement> {
        return $(this.wrapperElement);
    }

    /**
     * Returns a boolean describing if the mouse event occurred within this zone
     */
    hitTest(mouseEvent: MouseEvent): boolean {
        const bounds = this.wrapperElement.getBoundingClientRect();

        return (
            mouseEvent.clientX >= bounds.left &&
            mouseEvent.clientX <= bounds.right &&
            mouseEvent.clientY >= bounds.top &&
            mouseEvent.clientY <= bounds.bottom
        );
    }

    /**
     * Registers Event Interruption
     * eventName - name of interruption to add promise to
     * @todo: redo types once interruptions are enums
     */
    registerEventInterrupt(eventName: string, promise: JQuery.Deferred<void>): void {
        //we resolve the promise when the event is about to occur!
        if (typeof this.interruptions[eventName] === 'undefined') {
            this.interruptions[eventName] = [];
        }

        this.interruptions[eventName].push(promise);
    }

    private _onFullscreenAnnouncement(event: Event | CustomEvent): void {
        if (event.type === 'show-fullscreen-announcement') {
            this.wrapperElement.classList.add(FULLSCREEN_CSS_CLASS);
        } else if (event.type === 'hide-fullscreen-announcement') {
            this.wrapperElement.classList.remove(FULLSCREEN_CSS_CLASS);
        }
    }
}
